import { inject, Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { UserActions } from './user.actions';
import { UserApiService } from '../service/user-api.service';
import { firstValueFrom, tap } from 'rxjs';
import { CacheDataActions } from '../../../shared/store/cache-data/cache-data.actions';
import { CacheDataState } from '../../../shared/store/cache-data/cache-data.state';
import { User } from '../model/user';
import { UserPageCriteria } from '../model/user-page-criteria';
import { isDataCached } from '../../../shared/helper/is-data-cached';

export interface UserStateModel {
  user: Record<number, User>;
}

@State<UserStateModel>({
  name: 'user',
})
@Injectable()
export class UserState {
  private readonly userApiService: UserApiService = inject(UserApiService);
  private static readonly userListCacheKey = 'users-list';

  static page() {
    return createSelector(
      [CacheDataState.getDataByKey(UserState.userListCacheKey)],
      data => data
    );
  }

  static criteria() {
    return createSelector(
      [CacheDataState.getCriteriaByKey(UserState.userListCacheKey)],
      data => data
    );
  }

  static user(id: number) {
    return createSelector([UserState], (state: UserStateModel) => {
      return state.user?.[id];
    });
  }

  @Action(UserActions.GetPage, { cancelUncompleted: true })
  async getPage(
    ctx: StateContext<UserStateModel>,
    { criteria }: UserActions.GetPage
  ) {
    try {
      const cacheKey = UserState.userListCacheKey;

      if (isDataCached(cacheKey, criteria, 1000 * 5)) {
        await this.fetchPageData(ctx, criteria);
      }

      // @ts-ignore
      return;
    } catch (ex) {
      return Promise.reject(ex);
    }
  }

  @Action(UserActions.GetById)
  async getById(
    ctx: StateContext<UserStateModel>,
    { id }: UserActions.GetById
  ) {
    const state = ctx.getState();

    return this.userApiService.getById(id).pipe(
      tap((result: User) => {
        ctx.patchState({
          user: {
            ...state.user,
            [id]: result,
          },
        });
      })
    );
  }

  @Action(UserActions.Create)
  async create(
    ctx: StateContext<UserStateModel>,
    { data }: UserActions.Create
  ) {
    return this.userApiService
      .create(data)
      .pipe(
        tap(result => ctx.dispatch(new UserActions.InvalidateCache(result.id)))
      );
  }

  @Action(UserActions.Delete)
  async delete(ctx: StateContext<UserStateModel>, { id }: UserActions.Delete) {
    return this.userApiService
      .delete(id)
      .pipe(tap(_ => ctx.dispatch(new UserActions.InvalidateCache(id))));
  }

  @Action(UserActions.Edit)
  async edit(
    ctx: StateContext<UserStateModel>,
    { id, data }: UserActions.Edit
  ) {
    return this.userApiService
      .edit(id, data)
      .pipe(
        tap(result => ctx.dispatch(new UserActions.InvalidateCache(result.id)))
      );
  }

  @Action(UserActions.RefreshPage)
  async refreshPage(ctx: StateContext<UserStateModel>) {
    return this.fetchPageData(ctx);
  }

  @Action(UserActions.InvalidateCache)
  async invalidateCache(
    ctx: StateContext<UserStateModel>,
    { id }: UserActions.InvalidateCache
  ) {
    const state = ctx.getState();

    if (id && state.user?.[id]) {
      const { [id]: _, ...newUser } = state.user;

      ctx.setState({
        ...state,
        user: newUser,
      });
    }

    return ctx.dispatch(
      new CacheDataActions.InvalidateCache(UserState.userListCacheKey)
    );
  }

  private async fetchPageData(
    ctx: StateContext<UserStateModel>,
    criteria?: UserPageCriteria
  ) {
    const store = inject(Store);
    const updateCriteria =
      criteria ??
      store.selectSnapshot(
        CacheDataState.getCriteriaByKey(UserState.userListCacheKey)
      );

    const page = await firstValueFrom(
      this.userApiService.getPage(updateCriteria)
    );

    ctx.dispatch(
      new CacheDataActions.SetCachedData(
        UserState.userListCacheKey,
        page,
        updateCriteria
      )
    );
  }
}
